// Text for the public country pages (/country/:country), built only from the page's coverage data
// (/api/public/coverage/{country}): the number types on offer, activation times, outgoing-call availability and the
// country's regulations. Nothing here is invented per country, so every sentence stays true when the lists change.
import { dialCode } from './dialCodes';
import { flagCode } from './flags';

// Display order and plain-language explanations of each number kind.
export const KIND_INFO = {
  local: {
    name: 'Local', plural: 'local numbers', page: '/phone-numbers/local',
    about: (c) => `Geographic numbers with a local area code in ${c}. Callers in that area dial a familiar local number.`,
  },
  national: {
    name: 'National', plural: 'national numbers', page: '/phone-numbers/national',
    about: (c) => `Non-geographic numbers recognised across the whole of ${c}, not tied to one city or region.`,
  },
  mobile: {
    name: 'Mobile', plural: 'mobile numbers', page: '/phone-numbers/mobile',
    about: (c) => `Numbers from ${c}'s mobile ranges, which callers recognise as mobile numbers.`,
  },
  '2way': {
    name: '2-Way', plural: '2-way numbers', page: '/phone-numbers/2way',
    about: (c) => `Receive calls and place outgoing calls from the same ${c} number, so people you call see a local number they can call back.`,
  },
  uc: {
    name: 'UC', plural: 'UC numbers', page: '/phone-numbers/2way',
    about: () => 'Unified communications numbers: 2-way voice plus emergency calls, for a full business phone line.',
  },
  tollfree: {
    name: 'Toll-Free', plural: 'toll-free numbers', page: '/phone-numbers/toll-free',
    about: (c) => `Freephone numbers that callers in ${c} can dial without paying for the call.`,
  },
  uifn: {
    name: 'UIFN', plural: 'UIFN numbers', page: '/phone-numbers/uifn',
    about: (c) => `A Universal International Freephone Number (+800) that callers in ${c} can reach free of charge, with the same number in every participating country.`,
  },
};
export const KIND_ORDER = Object.keys(KIND_INFO);

/** URL path of a country page; encodes like the sitemap (Python quote with safe=""). */
export const countryPath = (name, sub = '') => `/country/${encodeURIComponent(name)
  .replace(/[!'()*]/g, (ch) => `%${ch.charCodeAt(0).toString(16).toUpperCase()}`)}${sub}`;

/** International dialling code for a country name, e.g. '+49' or '+1 268'; null when unknown. */
export const dialCodeFor = (name) => {
  const code = dialCode(flagCode(name));
  if (!code) return null;
  return code.length === 4 && code[0] === '1' ? `+1 ${code.slice(1)}` : `+${code}`;
};

/** 'a', 'a and b', 'a, b and c'. */
export const listJoin = (items) => (items.length < 2 ? items.join('')
  : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

const first = (options, max) => options.find((o) => o && o.length <= max) || options[options.length - 1];

/** Number kinds on offer in the country, in display order. */
export const kindsOf = (data) => {
  const have = new Set((data?.offers || []).map((o) => o.number_kind));
  return KIND_ORDER.filter((k) => have.has(k));
};

/** Activation times by kind, e.g. { local: '5-10 days', tollfree: 'Immediate' }. */
export const leadTimes = (data) => {
  const out = {};
  (data?.offers || []).forEach((o) => {
    if (o.lead_time && !out[o.number_kind]) out[o.number_kind] = o.lead_time;
  });
  return out;
};

const leadSummary = (data) => {
  const times = [...new Set(Object.values(leadTimes(data)))];
  if (!times.length) return null;
  if (times.length === 1) return times[0] === 'Immediate' ? 'immediately' : `in ${times[0]}`;
  return `in ${listJoin(times.map((t) => (t === 'Immediate' ? 'immediately' : t)))} depending on the type`;
};

const shortNames = (kinds) => kinds.map((k) => (k === 'uc' || k === 'uifn' ? KIND_INFO[k].name : KIND_INFO[k].name.toLowerCase()));

/** Page title (without the brand, which <Seo> adds): at most 60 characters with " | VOIP2". */
export const countryTitle = (country, dial) => first([
  dial && `Virtual Phone Numbers in ${country} (${dial})`,
  `Virtual Phone Numbers in ${country}`,
  `${country} Phone Numbers`,
  `${country} Numbers`,
], 60 - ' | VOIP2'.length);

/** Meta description (at most 155 characters) from the types actually on offer. */
export const countryDescription = (country, data, dial) => {
  const kinds = kindsOf(data).filter((k) => k !== 'uc' || !kindsOf(data).includes('2way'));
  const types = listJoin(shortNames(kinds));
  const where = dial ? `${country} (${dial})` : country;
  const outbound = data?.outbound_available ? ' Outgoing calls on 2-way numbers.' : '';
  return first([
    `Get ${types} numbers in ${where} from VOIP2.${outbound} See documents needed, activation times and how to order.`,
    `Get ${types} numbers in ${where} from VOIP2.${outbound} See documents needed and how to order.`,
    `Get ${types} numbers in ${where} from VOIP2. See documents needed and how to order.`,
    `Virtual phone numbers in ${where} from VOIP2: ${types}.`,
    `Virtual phone numbers in ${where} from VOIP2. See types, documents and how to order.`,
  ], 155);
};

/** Documents asked for in the country, de-duplicated across its regulation rows. */
export const allDocuments = (data) => [...new Set((data?.rules || []).flatMap((r) => r.documents || []))];

/** FAQs answered only from the page data. */
export const countryFaqs = (country, data, dial) => {
  const kinds = kindsOf(data);
  const has = (k) => kinds.includes(k);
  const docs = allDocuments(data);
  const lead = leadSummary(data);
  const typeNames = listJoin(shortNames(kinds));
  const faqs = [
    {
      q: `What types of phone number can I get in ${country}?`,
      a: `VOIP2 currently offers ${typeNames} numbers in ${country}.`,
    },
    {
      q: `Can I get a toll-free number in ${country}?`,
      a: has('tollfree')
        ? `Yes. Toll-free numbers are available in ${country}, so callers there can reach you without paying for the call.`
        : `Not at the moment: toll-free numbers in ${country} are not on our current list.${has('uifn') ? ` A UIFN (+800 international freephone number) is available instead.` : ''} Contact us if you need one and we will check.`,
    },
    {
      q: `Can I make outgoing calls from a ${country} number?`,
      a: data?.outbound_available
        ? `Yes. ${has('2way') && has('uc') ? '2-way and UC numbers' : has('uc') ? 'UC numbers' : '2-way numbers'} in ${country} can make outgoing calls as well as receive them.`
        : (has('2way') || has('uc'))
          ? `2-way numbers are listed in ${country}, but outgoing calls are not yet available on our rates list. Contact us and we will confirm before you order.`
          : `The ${country} numbers we list are for receiving calls. We do not currently offer 2-way numbers there.`,
    },
    {
      q: `What documents do I need for a ${country} number?`,
      a: docs.length
        ? `${country} asks for: ${docs.join('; ')}. You upload them through company verification (KYC) in the customer portal after ordering.`
        : `No documents are listed for ${country} at the moment. We will tell you if anything is needed when you order.`,
    },
  ];
  if (lead) {
    faqs.push({
      q: `How long does it take to activate a ${country} number?`,
      a: `Numbers in ${country} are usually activated ${lead}, once any required documents are approved.`,
    });
  }
  if (dial) {
    faqs.push({ q: `What is the international dialling code for ${country}?`, a: `The dialling code for ${country} is ${dial}.` });
  }
  return faqs;
};
